/**
 * Parse BeaCon API error envelopes into UI-friendly messages + CTAs.
 *
 * Backend shape (preferred):
 *   { error, code, message, cta?: { path, label }, details? }
 * Legacy FastAPI: { detail: string | object }
 */

export interface ApiCta {
  path: string
  label: string
}

export interface ParsedApiError {
  status?: number
  code: string
  message: string
  error: string
  cta?: ApiCta
  isSetupIncomplete: boolean
  raw?: unknown
}

const SETUP_CODES = new Set([
  'AWS_ACCOUNT_NOT_LINKED',
  'AWS_CREDENTIALS_MISSING',
  'AWS_ACCESS_DENIED',
  'SETUP_INCOMPLETE',
  'VPC_SNAPSHOT_UNAVAILABLE',
])

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function ctaFrom(value: unknown): ApiCta | undefined {
  const rec = asRecord(value)
  if (!rec) return undefined
  const path = typeof rec.path === 'string' ? rec.path : undefined
  const label = typeof rec.label === 'string' ? rec.label : undefined
  if (path && label) return { path, label }
  return undefined
}

function fromEnvelope(body: Record<string, unknown>, status?: number): ParsedApiError | null {
  const message = typeof body.message === 'string' ? body.message : undefined
  const code = typeof body.code === 'string' ? body.code : undefined
  if (!message && !code) return null
  const resolvedCode = code || 'REQUEST_FAILED'
  const cta = ctaFrom(body.cta) || defaultCtaForCode(resolvedCode)
  return {
    status,
    code: resolvedCode,
    message: message || 'Request failed',
    error: typeof body.error === 'string' ? body.error : 'Request Failed',
    cta,
    isSetupIncomplete: SETUP_CODES.has(resolvedCode),
    raw: body,
  }
}

function defaultCtaForCode(code: string): ApiCta | undefined {
  // AWS connectivity is Owner/Admin-only under Company Admin (not Settings).
  if (
    code === 'AWS_ACCOUNT_NOT_LINKED' ||
    code === 'VPC_SNAPSHOT_UNAVAILABLE' ||
    code === 'AWS_CREDENTIALS_MISSING' ||
    code === 'AWS_ACCESS_DENIED' ||
    code === 'SETUP_INCOMPLETE'
  ) {
    return { path: '/beacon/admin', label: 'Open Company Admin' }
  }
  return undefined
}

/** Extract a ParsedApiError from an Axios/fetch failure (or plain Error). */
export function parseApiError(err: unknown): ParsedApiError {
  const anyErr = err as {
    message?: string
    response?: { status?: number; data?: unknown }
  }
  const status = anyErr?.response?.status
  const data = anyErr?.response?.data
  const body = asRecord(data)

  if (body) {
    // Preferred envelope at top level
    const top = fromEnvelope(body, status)
    if (top) {
      // Soften legacy opaque 500s that only say "An unexpected error occurred"
      const legacyOpaque =
        status === 500 &&
        top.code !== 'INTERNAL_ERROR' &&
        /^an unexpected error occurred\.?$/i.test(top.message.trim())
      if (legacyOpaque) {
        return {
          ...top,
          code: 'INTERNAL_ERROR',
          message:
            'Something went wrong on the server. Try again, or ask an Owner/Admin to finish AWS setup in Company Admin.',
          cta: top.cta || { path: '/beacon/admin', label: 'Open Company Admin' },
        }
      }
      return top
    }

    // FastAPI detail: string | structured object
    const detail = body.detail
    if (typeof detail === 'string') {
      const looksInternal =
        detail.toLowerCase().includes('internal server error') ||
        status === 500
      return {
        status,
        code: status === 500 ? 'INTERNAL_ERROR' : 'REQUEST_FAILED',
        message: looksInternal
          ? 'Something went wrong on the server. If this continues, finish AWS setup in Company Admin or contact support@dynamoconsulting.solutions.'
          : detail,
        error: status === 500 ? 'Internal Server Error' : 'Request Failed',
        cta: status === 500 ? { path: '/beacon/admin', label: 'Open Company Admin' } : undefined,
        isSetupIncomplete: false,
        raw: body,
      }
    }
    const detailRec = asRecord(detail)
    if (detailRec) {
      const nested = fromEnvelope(detailRec, status)
      if (nested) return nested
      if (typeof detailRec.message === 'string') {
        return {
          status,
          code: typeof detailRec.code === 'string' ? detailRec.code : 'REQUEST_FAILED',
          message: detailRec.message,
          error: typeof detailRec.error === 'string' ? detailRec.error : 'Request Failed',
          cta: ctaFrom(detailRec.cta),
          isSetupIncomplete: SETUP_CODES.has(String(detailRec.code || '')),
          raw: body,
        }
      }
    }
  }

  const fallback =
    (typeof anyErr?.message === 'string' && anyErr.message) ||
    'Request failed'
  const isInternal = status === 500 || /internal server error/i.test(fallback)
  return {
    status,
    code: isInternal ? 'INTERNAL_ERROR' : 'REQUEST_FAILED',
    message: isInternal
      ? 'Something went wrong on the server. Try again, or finish AWS setup in Company Admin.'
      : fallback,
    error: isInternal ? 'Internal Server Error' : 'Request Failed',
    cta: isInternal ? { path: '/beacon/admin', label: 'Open Company Admin' } : undefined,
    isSetupIncomplete: false,
    raw: data,
  }
}

/** Short toast-friendly message. */
export function apiErrorMessage(err: unknown): string {
  return parseApiError(err).message
}
